import { mdiCloudPrint, mdiFeatureSearch, mdiMapCheck, mdiTagHeartOutline, mdiDomain, mdiArrowUpBoldCircleOutline, mdiHanger } from "@mdi/js";
import type { MainViewMode } from "~/types/entity/cWebCatalog";
import type { Role } from "~/types/entity/user";

// 状態を保持しない純粋関数として表現できるページと権限の関係の記述
// 権限表 順番に権限を増やしたいため const 変数割り当てをしている
const abovePlatinumRoles: Role[] = ["Platinum"];
// const aboveGoldRoles: Role[] = [...abovePlatinumRoles, "Gold"];
// const aboveSilverRoles: Role[] = [...aboveGoldRoles, "Silver"];
// const aboveBronzeRoles: Role[] = [...aboveSilverRoles, "Bronze"];

/**
 * EntraID / AzureAD B2C ログインを使うなら true
 * C105以前の circle.ms 共通認証クッキーを使うなら false
 */
export const useEntraId2Token = true;

/**
 * メニュー表示レベル
 * null : 非表示
 * 1 : トップメニューとして表示
 * 2 : サブメニューとして表示 (2 以上の数値をどのように表示するかは component に依存する)
 */
type menuLevel = null | "main" | "submenu"

type NavigationSetting = {
  viewMode: MainViewMode
  /**
   * 遷移先のパス
   * メニューとして存在しない (詳細画面など) の場合は null
   */
  route: string | null
  /*
   * 保持していれば利用可能になる Role の配列
   * null の場合は Role を要求しない
   */
  roles: Role[] | null
  icon?: string
  pcMenu: menuLevel
  mobileMenu: menuLevel
  isBooth?: boolean
  // C105までは 企業ブース表示期間前に企業ブース表示させないために使っていた
  // disabled?: boolean
  /**
   * 外部リンクの場合は true
   */
  isExternal?: boolean
}

export const navigationSettingsAll: NavigationSetting[] = [
  {
    viewMode: "TOP_PAGE",
    route: "/",
    roles: null,
    icon: mdiArrowUpBoldCircleOutline,
    pcMenu: "main",
    mobileMenu: "submenu",
  },
  {
    viewMode: "CIRCLE_LIST",
    route: "/circle/list",
    roles: abovePlatinumRoles,
    icon: mdiFeatureSearch,
    pcMenu: "main",
    mobileMenu: "main",
    isBooth: false
  },
  {
    viewMode: "BOOTH_LIST",
    route: "/booth",
    roles: abovePlatinumRoles,
    icon: mdiDomain,
    pcMenu: "main",
    mobileMenu: "submenu",
    isBooth: true, // !webCatalogBoothData.isPublishedBooth()
  },
  {
    viewMode: "FAVORITE",
    route: "/user/favorites",
    roles: abovePlatinumRoles,
    icon: mdiTagHeartOutline,
    pcMenu: "main",
    mobileMenu: "main",
  },
  {
    viewMode: "COMIKE_MAP",
    route: "/map",
    roles: abovePlatinumRoles,
    icon: mdiMapCheck,
    pcMenu: "main",
    mobileMenu: "main",
  },
  {
    viewMode: "COMIKE_PRINT",
    route: "/print",
    roles: abovePlatinumRoles,
    icon: mdiCloudPrint,
    pcMenu: "main",
    mobileMenu: "submenu",
    isBooth: false
  },
  {
    viewMode: "COMIKE_PRINT_DETAIL", // 実際の印刷画面
    route: null, // print-type-[printType]
    roles: abovePlatinumRoles,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "LOGIN",
    route: useEntraId2Token ? "/auth/msal" : "/auth/login",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "LOGOUT",
    route: useEntraId2Token ? "/auth/msal-logout" : "/auth/auth2-logout",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "SETTING",
    route: "/account/settings",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "CIRCLE_DETAIL",
    route: null, // circle/[id].vue
    roles: abovePlatinumRoles,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "BOOTH_DETAIL",
    route: null, // booth/[id].vue
    roles: abovePlatinumRoles,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "PAYMENT_START",
    route: "/pay",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "RESTRICTION",
    route: "/restriction",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "HELP",
    route: "/docs/help",
    roles: null,
    pcMenu: null,
    mobileMenu: null,
  },
  {
    viewMode: "COMICOS",
    route: "/comicos",
    roles: null,
    icon: mdiHanger,
    pcMenu: "main",
    mobileMenu: "submenu",
  },
];

export const isAuthorizedRole = (mode: MainViewMode, roles: Role[]): boolean => {
  const setting = navigationSettingsAll.filter(x => x.viewMode == mode)[0];
  if (setting === undefined) {
    return false;
  }
  if (setting.roles === null) {
    return true;
  }
  return roles.some(x => setting.roles?.includes(x));
};

export const getNavigationRoute = (mode: MainViewMode): string | null | undefined =>
  navigationSettingsAll.filter(x => x.viewMode == mode).map(x => x.route)[0];

// 以下はコンテキスト依存の少ない共通ユーティリティ
/**
 * n個ずつに配列を分割する
 * @param arr
 * @param n
 */
export function* chunksGenerator<T>(arr: T[], n: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += n) {
    yield arr.slice(i, i + n);
  }
}

/**
 * 文字列配列に変換する。単一の値だったとしても配列とする。ただし値がなかった場合は undefined
 * @param v
 * @returns
 */
export const convertToStringArray = (v: string | (string | null)[] | null | undefined): string[] | undefined => {
  if (Array.isArray(v)) {
    return v.map(item => (typeof item === "string" ? item : "")).filter(item => item !== "");
  }
  if (typeof v === "string") {
    return [v];
  }
  return undefined;
};

export const getTanStackQueryKey = (url: string, body: unknown, acceptLanguage?: string): string[] => {
  const array = [url, (acceptLanguage ?? "")];
  const bodyObject = body as Record<string, unknown>;
  for (const [key, value] of Object.entries(bodyObject)) {
    if (Array.isArray(value)) {
      array.push(key, value.join(","));
    }
    else {
      array.push(key, value as string);
    }
    array.push(key, value as string);
  }
  return array;
};
